import React, { useState } from "react";
import { toast } from "react-toastify";
import Search from "../../components/form/Search";
import Table from "../../components/elements/table/Table";
import { setTitle } from "../../helpers/MetaTag";
import { handleFilterChange } from "../../helpers";

import useFetch from "../../hooks/useFetch";
import { useDebounce } from "../../hooks/useDebounce";
import LimitSelect from "../../components/elements/LimitSelect";
import useMutation from "../../hooks/useMutation";
import Switch from "../../components/form/Switch";
import Badge from "../../components/elements/Badge";
import {
  emailTemplateList,
  emailTemplateStatusChange,
} from "../../services/emailService";
import ManageEmailTemplateModal from "../../components/sections/admin/emailTemplate/modals/ManageEmailTemplateModal";
import { useGetPermission } from "../../hooks/useGetPermission";

const EmailTemplate = () => {
  setTitle("Vpeters Admin | Email template");
  const { permission, status } = useGetPermission("emailTemplates");
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const emailName = {
    "send-otp": "Send Otp",
    "welcome-email-user": "Welcome Email",
    "reset-pass-link-invite": "Reset Password",
    donation: "Donation",
    "send-contact-us-rec": "Contact Us",
    "email-change": "Change Email",
    scholarship_submission: "Scholarship Submission",
    application_status_track: "Application Status Track",
    application_status_reminder: "Application Status Reminder",
    "donor-send-invite": "Donor Send Invite",
    "contact-us-admin-notify": "Contact Us Admin Notify",
    "user-account-delete": "User Account Delete",
    scholarship_submission_admin_notify: "Scholarship Submission Admin Notify",
    "send-message": "Send Message",
    donation_received: "Donation Received",
  };

  const { data, error, isLoading, updateData, isFetching, refetch } = useFetch({
    dependencies: ["emailtemplate", filters, search, permission?.list],
    fetchFunction: async () => {
      const res = await emailTemplateList({
        ...filters,
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!permission?.list && status === "ok",
  });

  //   Application Form status change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, status }) => {
        const res = await emailTemplateStatusChange({
          ids: [id],
          status,
        });
        return { res, payload: { id, status } };
      },
      onSuccess: ({ res, payload }) => {
        if (res?.status !== 200) {
          updateData((pre) => {
            pre.data = (pre?.data || []).map((elm) =>
              elm?._id === payload?.id
                ? { ...elm, status: payload?.status === 1 ? 2 : 1 }
                : elm
            );
            return { ...pre };
          });
          toast.warning(res?.message || res?.error);
        }
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const tableHeadData = [
    {
      _id: 1,
      name: "Email For",
      align: "left",
    },
    // {
    //   _id: 2,
    //   name: "Subject",
    //   align: "left",
    // },
    {
      _id: 3,
      name: "From Name",
      align: "left",
    },
    {
      _id: 4,
      name: "Status",
      align: "left",
    },
    ...(permission?.edit
      ? [
          {
            _id: 5,
            name: "Action",
            align: "left",
          },
        ]
      : []),
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            (
              <div
                dangerouslySetInnerHTML={{
                  __html: emailName[item?.code] || "-",
                }}
              ></div>
            ) || "-",
        },
        // {
        //   _id: 2,
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   data:
        //     (
        //       <div
        //         dangerouslySetInnerHTML={{
        //           __html: item?.subject || "-",
        //         }}
        //       ></div>
        //     ) || "-",
        // },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            (
              <div
                dangerouslySetInnerHTML={{ __html: item?.fromName || "-" }}
              ></div>
            ) || "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex gap-2 items-center">
              <Switch
                className={
                  "!w-10 !h-5 before:!w-4 before:!h-4 peer-checked:before:!left-[22px]"
                }
                checked={item?.status === 1}
                onChange={() => {
                  if (permission?.changeStatus && status === "ok") {
                    updateData((pre) => {
                      pre.data = (pre?.data || []).map((elm) =>
                        elm?._id === item?._id
                          ? { ...elm, status: elm?.status === 1 ? 2 : 1 }
                          : elm
                      );
                      return { ...pre };
                    });
                    statusChangeHandler({
                      id: item?._id,
                      status: item?.status === 1 ? 2 : 1,
                    });
                  } else {
                    toast.info(
                      "You don't have permission to change this status."
                    );
                  }
                }}
                disabled={!permission?.changeStatus}
              />
              <Badge
                type={item?.status === 1 ? "success" : "danger"}
                label={item?.status === 1 ? "Active" : "Inactive"}
              />
            </div>
          ),
        },
        ...(permission?.edit
          ? [
              {
                _id: 5,
                type: "action",
                align: "left",
                cellClass: "w-[5%]",
                actionData: [
                  ...(permission?.edit && status === "ok"
                    ? [
                        {
                          _id: 1,
                          name: "Edit",
                          icon: "fa-regular fa-pen-to-square",
                          onClick: () => {
                            setModalOpen({ isOpen: true, data: item });
                          },
                          standout: false,
                        },
                      ]
                    : []),
                ],
              },
            ]
          : []),
      ],
    };
  });

  // add or edit Application Form =================>
  function applicationFormManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          updateData((pre) => ({
            ...pre,
            data: [res?.data, ...(pre?.data || [])],
          }));
          refetch();
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        updateData((pre) => ({
          ...pre,
          data: pre?.data?.map((item) =>
            item?._id === res?.data?._id ? res?.data : item
          ),
        }));
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="space-y-8">
        <div className="font-bold text-center text-lg xl:text-3xl text-chestnut-rose">
          Email Templates
        </div>

        <div className="flex items-center gap-3">
          <div className="w-36">
            <LimitSelect
              value={filters?.limit}
              allValue={data?.metadata?.totalDocs}
              onChange={(val) => handleFilterChange("limit", val, setFilters)}
            />
          </div>
          <div className="w-72">
            <Search
              placeholder={"Search here"}
              search={(val) => {
                setKeyword(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
          </div>
          {/* <div className="relative">
            <Button
              buttonClasses={
                "!bg-transparent border border-transparent !h-10 hover:!bg-chestnut-rose/10 hover:!border-chestnut-rose/10"
              }
              buttonLabel={"Create New"}
              buttonLabelClasses={"font-medium !text-sm text-chestnut-rose"}
              buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
            />
          </div> */}
        </div>
      </div>
      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={(val) => {
          handleFilterChange("page", val, setFilters);
          setSelectedItems([]);
        }}
        handleOrdering={(val) => {
          console.log(val);
        }}
        isFetching={isFetching}
        isOrderingDisabled={true}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
      />
      <ManageEmailTemplateModal
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={applicationFormManageResponse}
      />
    </div>
  );
};

export default EmailTemplate;
